// Telemetría de producto: qué pantallas se ven y dónde se atasca la gente.
// Nunca cuánto dinero hay de por medio.
//
// El servidor descarta lo que no reconoce (backend/telemetry.py), así que un
// descuido aquí no filtra nada. Aun así este módulo tampoco lo permite: la
// función pública sólo acepta los campos estructurales, y cualquier otra clave
// se queda fuera antes de salir del navegador. Dos puertas, no una — la de
// dentro es la que manda, pero la de fuera evita que el dato viaje siquiera.
//
// No hay proveedor externo. Esto va a nuestra propia API y se queda ahí.
// A propósito NO se usa el cliente de `lib/api`: su interceptor de respuesta
// redirige a /activar cuando llega un 402, y un evento de telemetría que se
// cruza en segundo plano echaría a alguien de la pantalla en mitad de lo que
// está haciendo. La telemetría no puede tener efectos. Aquí se habla con la API
// a pelo, se ignoran todos los errores, y no hay interceptores de por medio.
import { API } from "@/lib/api";

const SESSION_KEY = "td_telemetry_session";
const LOTE_MAX = 25;
const VACIADO_MS = 10_000;

let cola = [];
let temporizador = null;

function enviar(lote, alSalir = false) {
  let token = null;
  try { token = localStorage.getItem("td_token"); } catch { /* modo privado */ }
  if (!token) return Promise.resolve();
  return fetch(`${API}/telemetry`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ events: lote }),
    // `keepalive` deja que la petición sobreviva al cierre de la pestaña. Sin
    // esto se perderían justo los eventos de abandono, que son los únicos que
    // dicen dónde se rindió alguien — y los datos saldrían sesgados hacia el
    // final feliz.
    keepalive: alSalir,
  }).catch(() => {});
}

function sessionId() {
  try {
    let s = sessionStorage.getItem(SESSION_KEY);
    if (!s) {
      s = Math.random().toString(36).slice(2, 14);
      sessionStorage.setItem(SESSION_KEY, s);
    }
    return s;
  } catch {
    // Modo privado: sin sesión se pierde el encadenado de eventos, no la app.
    return undefined;
  }
}

function vaciar(alSalir = false) {
  if (!cola.length) return;
  // Se saca de la cola ANTES de enviar. Si el envío falla, esos eventos se
  // pierden y no se reintentan: entre perder telemetría y contar dos veces un
  // flujo terminado, se pierde. Un dato inflado se lee como un hallazgo.
  const lote = cola.splice(0, LOTE_MAX);
  enviar(lote, alSalir);
}

function programar() {
  if (temporizador) return;
  temporizador = setTimeout(() => {
    temporizador = null;
    vaciar();
  }, VACIADO_MS);
}

/**
 * Registra un evento estructural.
 * @param {string} event  nombre del catálogo (backend/telemetry.py)
 * @param {{screen?: string, step?: string, duration_ms?: number,
 *          back_presses?: number, cancelled?: boolean}} [campos]
 */
export function track(event, campos = {}) {
  if (!event) return;
  const { screen, step, duration_ms, back_presses, cancelled } = campos;
  cola.push({
    event,
    ...(screen !== undefined && { screen }),
    ...(step !== undefined && { step }),
    ...(duration_ms !== undefined && { duration_ms: Math.round(duration_ms) }),
    ...(back_presses !== undefined && { back_presses }),
    ...(cancelled !== undefined && { cancelled }),
    session_id: sessionId(),
  });
  if (cola.length >= LOTE_MAX) vaciar();
  else programar();
}

// Cerrar la pestaña no debe tirar los eventos del último minuto, que son justo
// los del momento en que alguien se rindió.
export function initProductTelemetry() {
  const salida = () => {
    if (temporizador) { clearTimeout(temporizador); temporizador = null; }
    vaciar(true);
  };
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") salida();
  });
  window.addEventListener("pagehide", salida);
  track("app_opened");
}

// Cronómetro por paso, para `duration_ms` y `back_presses`. Vive aquí para que
// cada flujo no se invente su propia forma de medir el tiempo.
export function crearCronometro() {
  let inicio = Date.now();
  let atras = 0;
  return {
    reiniciar() { inicio = Date.now(); },
    marcarAtras() { atras += 1; },
    transcurrido() { return Date.now() - inicio; },
    atras() { return atras; },
  };
}
